/**
 * Spotify for Artists Agent
 * Frontend service that communicates with backend agent controller
 * Uses browser automation and vision AI to extract playlist data
 * from user's own Spotify for Artists dashboard
 */

class SpotifyArtistsAgent {
  constructor() {
    this.sessionId = null;
    this.isAuthenticated = false;
    this.sessionTimeout = 30 * 60 * 1000; // 30 minutes
    this.lastActivity = null;
    this.baseURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    this.needsVerification = false; // Track if email verification is needed
  }

  /**
   * Make API call with proper error handling
   */
  async apiCall(endpoint, method = 'GET', data = null) {
    const url = `${this.baseURL}/api/spotify-artists-agent${endpoint}`;
    
    console.log(`🌐 API Call: ${method} ${url}`);
    
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    if (data && (method === 'POST' || method === 'PUT')) {
      options.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(url, options);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ API Error: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`✅ API Response:`, result);
      return result;
      
    } catch (error) {
      console.error(`❌ API Call failed:`, error);
      throw error;
    }
  }

  /**
   * Initialize the agent
   */
  async initialize() {
    console.log('🤖 Initializing Spotify for Artists Agent...');
    
    try {
      // Call backend to initialize agent session
      const response = await this.apiCall('/initialize', 'POST', {});

      if (response.success) {
        this.sessionId = response.sessionId;
        this.lastActivity = Date.now();
        
        if (response.needsVerification) {
          // Email verification required
          this.needsVerification = true;
          this.isAuthenticated = false;
          
          console.log('📧 Email verification required');
          return { 
            success: true, 
            needsVerification: true, 
            message: response.message,
            sessionId: this.sessionId
          };
        } else {
          // Login successful
          this.isAuthenticated = true;
          this.needsVerification = false;
          
          console.log('✅ Agent initialized successfully, session ID:', this.sessionId);
          return { success: true, needsVerification: false };
        }
      } else {
        console.error('❌ Backend initialization failed:', response.message);
        return { success: false, message: response.message };
      }
      
    } catch (error) {
      console.error('❌ Failed to initialize agent:', error);
      return { success: false, message: error.message };
    }
  }

  /**
   * Submit email verification code
   */
  async submitVerificationCode(verificationCode) {
    console.log('📧 Submitting email verification code...');
    
    try {
      if (!this.sessionId) {
        throw new Error('No active session for verification');
      }

      const response = await this.apiCall('/verify-email', 'POST', {
        sessionId: this.sessionId,
        verificationCode
      });

      if (response.success) {
        this.isAuthenticated = true;
        this.needsVerification = false;
        this.lastActivity = Date.now();
        
        console.log('✅ Email verification successful');
        return { success: true, message: response.message };
      } else {
        console.error('❌ Email verification failed:', response.message);
        return { success: false, message: response.message };
      }
      
    } catch (error) {
      console.error('❌ Failed to submit verification code:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to submit verification code' 
      };
    }
  }

  /**
   * Extract comprehensive dashboard data using vision
   */
  async extractDashboardData() {
    console.log('🔍 Extracting comprehensive dashboard data...');
    
    try {
      if (!this.sessionId || !this.isAuthenticated) {
        throw new Error('Agent not initialized or authenticated');
      }

      // Get userId from localStorage (DIZEE token)
      let userId = null;
      try {
        const dizeeToken = localStorage.getItem('dizeeToken');
        if (dizeeToken) {
          const tokenData = JSON.parse(dizeeToken);
          userId = tokenData.user?.id || tokenData.userId || null;
        }
      } catch (error) {
        console.warn('Could not extract userId from DIZEE token:', error);
      }

      // Call backend to extract dashboard data
      const response = await this.apiCall('/extract-dashboard', 'POST', {
        sessionId: this.sessionId,
        userId: userId
      });

      if (response.success) {
        this.lastActivity = Date.now();
        console.log('✅ Successfully extracted comprehensive dashboard data');
        console.log('📊 Data extracted from pages:', Object.keys(response.data).filter(key => 
          !['extractedAt', 'userId', 'artistName', 'verificationStatus', 'dataVersion', 'extractionSession'].includes(key) 
          && response.data[key] && !response.data[key].error
        ));
        
        if (response.storage) {
          console.log('💾 Data storage info:', response.storage);
          if (response.storage.saved) {
            console.log('✅ Data saved to database with ID:', response.storage.dataId);
            console.log('🔗 Available for apps:', response.storage.availableForApps);
          }
        }
        
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to extract dashboard data');
      }

    } catch (error) {
      console.error('❌ Failed to extract dashboard data:', error);
      throw error;
    }
  }

  /**
   * Get complete playlist data for a track
   */
  async getTrackPlaylistData(trackName, artistName) {
    console.log(`🎯 Getting complete playlist data for "${trackName}" by "${artistName}"`);
    
    try {
      if (!this.sessionId || !this.isAuthenticated) {
        throw new Error('Agent not initialized or authenticated');
      }

      // Call backend to get playlist data
      const response = await this.apiCall('/track-playlists', 'POST', {
        sessionId: this.sessionId,
        trackName,
        artistName
      });

      if (response.success) {
        this.lastActivity = Date.now();
        console.log(`✅ Successfully extracted ${response.data.length} playlist placements`);
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to get playlist data');
      }

    } catch (error) {
      console.error('❌ Failed to get track playlist data:', error);
      return [];
    }
  }

  /**
   * Login to Spotify for Artists (handled by initialize method)
   */
  async login() {
    console.log('🔐 Login is handled during initialization');
    return this.isAuthenticated;
  }

  /**
   * Keep session alive
   */
  async keepAlive() {
    if (!this.sessionId || !this.isAuthenticated) return;
    
    try {
      const response = await this.apiCall('/keep-alive', 'POST', {
        sessionId: this.sessionId
      });
      
      if (response.success) {
        this.lastActivity = Date.now();
        console.log('💓 Session kept alive');
      }
      
    } catch (error) {
      console.warn('⚠️ Failed to keep session alive:', error);
    }
  }

  /**
   * Check if session is still valid
   */
  isSessionValid() {
    if (!this.lastActivity) return false;
    return (Date.now() - this.lastActivity) < this.sessionTimeout;
  }

  /**
   * Cleanup resources
   */
  async cleanup() {
    console.log('🧹 Cleaning up Spotify for Artists Agent...');
    
    try {
      if (this.sessionId) {
        await this.apiCall(`/session/${this.sessionId}`, 'DELETE');
      }
      
      this.sessionId = null;
      this.isAuthenticated = false;
      this.lastActivity = null;
      
      console.log('✅ Agent cleanup complete');
      
    } catch (error) {
      console.error('❌ Error during cleanup:', error);
    }
  }

  /**
   * Get agent status
   */
  getStatus() {
    return {
      isInitialized: !!this.sessionId,
      isAuthenticated: this.isAuthenticated,
      needsVerification: this.needsVerification,
      sessionValid: this.isSessionValid(),
      lastActivity: this.lastActivity
    };
  }

  /**
   * Get status from backend
   */
  async getBackendStatus() {
    if (!this.sessionId) {
      return this.getStatus();
    }
    
    try {
      const response = await this.apiCall(`/status/${this.sessionId}`, 'GET');
      
      if (response.success) {
        const backendStatus = response.status;
        
        // Update local state
        this.isAuthenticated = backendStatus.isAuthenticated;
        this.lastActivity = backendStatus.lastActivity;
        
        // Merge with local verification state
        return {
          ...backendStatus,
          needsVerification: this.needsVerification
        };
      }
      
    } catch (error) {
      console.warn('⚠️ Failed to get backend status:', error);
    }
    
    return this.getStatus();
  }
}

export default new SpotifyArtistsAgent(); 